import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withSnackbar } from "notistack";
import Alert from "@mui/material/Alert";
import { Logger, toastActions } from "@base-provider";
import SFIconButton from "../buttons/iconButton";
import { SFStack } from "../grid";
import SFTypography from "../typography";
import useStyled from "../../hooks/useStyled";
import SFHaloIconWrapper from "../icons/halo";
import {
  SFErrorIcon,
  SFInfoRoundedIcon,
  SFCompletedIcon,
  SFWarningIcon,
  SFCloseIcon,
  SFNotificationIcon,
} from "../icons";
import { Notification_Actions } from "./../../constants/notificationTypes";
import { notificationActions } from "../../redux/reducers/notificationReducer";
import { useDispatch } from "react-redux";

const { styled } = useStyled();

const AlertStyled = styled(Alert, {
  shouldForwardProp: (prop) => prop !== "hasDetail",
})(({ theme, hasDetail }) => ({
  backgroundColor: theme.palette.grey450.main,
  border: "none",
  borderRadius: 4,
  width: "100%",
  minWidth: 400,
  height: "100%",
  padding: theme.spacing(1, 2),
  minHeight: 80,
  ...(hasDetail && {
    width: 700,
  }),
  "& .MuiAlert-action": {
    paddingRight: theme.spacing(1),
    justifyContent: "center",
    alignItems: "center",
  },
  "& .MuiAlert-icon": {
    alignSelf: "center",
  },
}));

const StyledButton = styled(SFIconButton)(({ theme }) => ({
  border: "1px solid #fff !important",
  backgroundColor: "transparent",
  borderRadius: 3,
  padding: theme.spacing(1, 1.5),
  textTransform: "uppercase",
  "&:hover": {
    backgroundColor: theme.palette.grey400.main,
  },
}));

const SFSTackStyled = styled(SFStack)(({ theme }) => ({
  paddingRight: theme.spacing(5),
  paddingLeft: theme.spacing(1),
  height: "100%",
}));

const SFTypographyStyled = styled(SFTypography)({
  overflow: "hidden",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
});

const StyledDetails = styled("div")(({ theme }) => ({
  "& a": {
    color: "#4EBEEB !important",
    textDecoration: "none",
  },
}));

const customIcons = {
  error: (
    <SFHaloIconWrapper variant="red">
      <SFErrorIcon size="large" />
    </SFHaloIconWrapper>
  ),
  info: (
    <SFHaloIconWrapper variant="grey">
      <SFInfoRoundedIcon size="large" />
    </SFHaloIconWrapper>
  ),
  success: (
    <SFHaloIconWrapper variant="green">
      <SFCompletedIcon size="large" />
    </SFHaloIconWrapper>
  ),
  warning: (
    <SFHaloIconWrapper variant="orange">
      <SFWarningIcon size="large" />
    </SFHaloIconWrapper>
  ),
  reminder: (
    <SFHaloIconWrapper variant="blue">
      <SFNotificationIcon size="large" />
    </SFHaloIconWrapper>
  ),
};

const Notifier = ({
  notifications,
  removeMessage,
  enqueueSnackbar,
  closeSnackbar,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const alertContent = (
    type = "info",
    key,
    message,
    detail,
    title,
    actionUrl = "profile",
    actionLabel,
    customAction
  ) => {
    const handleActionClick = () => {
      switch (customAction) {
      case Notification_Actions.NAVIGATE:
        navigate(actionUrl);
        closeSnackbar(key);
        break;
      case Notification_Actions.VIEW:
        dispatch(notificationActions.setNotificationPanel(true));
        closeSnackbar(key);
        break;
      default:
        break;
      }
    };

    return (
      <AlertStyled
        icon={customIcons[type]}
        hasDetail={detail}
        action={
          <SFStack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            alignItems="center"
          >
            {customAction && actionUrl && (
              <StyledButton onClick={() => handleActionClick()}>
                <SFTypography variant="body2">{actionLabel}</SFTypography>
              </StyledButton>
            )}
            <SFIconButton
              variant="transparent"
              onClick={() => closeSnackbar(key)}
            >
              <SFCloseIcon />
            </SFIconButton>
          </SFStack>
        }
      >
        <SFSTackStyled direction="row" alignItems="center">
          <SFStack direction="column" spacing={1}>
            {title && <SFTypography variant="body2bold">{title}</SFTypography>}
            <StyledDetails
              onClick={(e) => {
                // Check if the clicked element has an href attribute
                if (e.target.href) closeSnackbar(key);
              }}
            >
              <SFTypographyStyled variant="body1">
                {detail ?? message}
              </SFTypographyStyled>
            </StyledDetails>
          </SFStack>
        </SFSTackStyled>
      </AlertStyled>
    );
  };

  useEffect(() => {
    Logger.of("App.Core.Notifier").info({ notifications });
    notifications.forEach((n) => {
      enqueueSnackbar(n.message, {
        key: n.id,
        preventDuplicate: true,
        disableWindowBlurListener: false,
        variant: n.type,
        onClose: (_event, _reason, key) => removeMessage(key),
        content: alertContent(
          n.type,
          n.id,
          n.message,
          n.detail,
          n.title,
          n.actionUrl,
          n.actionLabel,
          n.customAction
        ),
      });
    });
  }, [notifications]);

  return null;
};

const mapStateToProps = (store) => ({
  notifications: store.toast.messages,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ removeMessage: toastActions.removeMessage }, dispatch);

export default withSnackbar(
  connect(mapStateToProps, mapDispatchToProps)(Notifier)
);
